import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { useNavigate } from 'react-router-dom';
import { catalog } from '../api/index.js';
import { Card } from '../components/ui/card.js';
import { Loader2, Search, AlertCircle, Tag } from 'lucide-react';

export function GenreList() {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');

  const { data, isLoading, error } = useQuery({
    queryKey: ['catalog-genres'],
    queryFn: catalog.genres,
    staleTime: 10 * 60 * 1000,
  });

  const all = data?.genres ?? [];
  const filtered = search
    ? all.filter(g => g.name.toLowerCase().includes(search.toLowerCase()))
    : all;

  return (
    <div className="p-8 max-w-4xl mx-auto">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
          <Tag size={22} className="text-brand-600" /> Genres
        </h1>
        <p className="text-gray-500 text-sm mt-1">{all.length} genres in catalog</p>
      </div>

      <div className="relative mb-4">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input
          type="text"
          placeholder="Search genres…"
          value={search}
          onChange={e => setSearch(e.target.value)}
          className="w-full pl-9 pr-4 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
        />
      </div>

      <Card>
        {isLoading ? (
          <div className="flex items-center justify-center py-16 text-gray-400">
            <Loader2 className="animate-spin mr-2" /> Loading…
          </div>
        ) : error ? (
          <div className="flex items-center justify-center py-16 text-red-500 gap-2 text-sm">
            <AlertCircle size={16} /> Failed to load genres
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-gray-100 bg-gray-50">
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Genre</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                  <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-50">
                {filtered.map(g => (
                  <tr key={g._id} onClick={() => navigate(`/genres/${g._id}`)} className="hover:bg-gray-50 cursor-pointer transition-colors">
                    <td className="px-4 py-3 font-medium text-gray-900">{g.name}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{g.slug ?? '—'}</td>
                    <td className="px-4 py-3 text-gray-400 text-xs font-mono">{g._id}</td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr>
                    <td colSpan={3} className="px-5 py-12 text-center text-gray-400">
                      {search ? 'No genres match the search' : 'No genres found — try syncing the catalog'}
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>
        )}
      </Card>
    </div>
  );
}
